/**
 * Building blocks shared by the Grade 1–4 templates (grade01.ts … grade04.ts).
 *
 * Scoped to this grade range (hence the file name) so it never collides with
 * helpers owned by other grade files. Everything here is pure; the only
 * randomness is the Rng passed in, so templates stay deterministic.
 */
import { int } from '../../math/rational.ts';
import type { Rng } from '../../math/rng.ts';
import type { GeneratedProblemCore, MathMarkup } from '../../types.ts';

export type Instruction = NonNullable<GeneratedProblemCore['instruction']>;

/** Linear interpolation from `lo` (difficulty 0) to `hi` (difficulty 1); difficulty is clamped to [0, 1]. */
export function lerp(lo: number, hi: number, difficulty: number): number {
  const t = Math.min(1, Math.max(0, difficulty));
  return lo + (hi - lo) * t;
}

/** `lerp` rounded to a whole number — for difficulty-scaled range bounds. */
export function scaled(lo: number, hi: number, difficulty: number): number {
  return Math.round(lerp(lo, hi, difficulty));
}

/**
 * Split `total` into two unequal whole parts, each ≥ `min`. Unequal so the
 * answer is never a copy of the other number in the prompt. Needs
 * `total ≥ 2·min + 1`.
 */
export function splitUnequal(rng: Rng, total: number, min = 1): [number, number] {
  const first = rng.intExcept(min, total - min, total % 2 === 0 ? [total / 2] : []);
  return [first, total - first];
}

/**
 * Column subtraction with the classic "smaller-from-larger" bug: each column
 * takes the bigger digit minus the smaller one instead of borrowing
 * (42 − 17 → 35). Equals the true difference when no borrow is needed.
 */
export function smallerFromLarger(top: number, bottom: number): number {
  let result = 0;
  let place = 1;
  for (let t = top, b = bottom; t > 0 || b > 0; t = Math.floor(t / 10), b = Math.floor(b / 10)) {
    result += Math.abs((t % 10) - (b % 10)) * place;
    place *= 10;
  }
  return result;
}

/** Column addition that forgets to carry: each column keeps only its units digit (48 + 35 → 73). */
export function addWithoutCarrying(a: number, b: number): number {
  let result = 0;
  let place = 1;
  for (let x = a, y = b; x > 0 || y > 0; x = Math.floor(x / 10), y = Math.floor(y / 10)) {
    result += (((x % 10) + (y % 10)) % 10) * place;
    place *= 10;
  }
  return result;
}

/**
 * One step of a counting pattern done with a column slip: forgot to carry when
 * counting up (18 + 5 → 13), or took smaller-from-larger when counting down
 * (43 − 5 → 42). Equals the correct next term when no regrouping is involved.
 */
export function columnSlip(from: number, step: number, backward: boolean): number {
  return backward ? smallerFromLarger(from, step) : addWithoutCarrying(from, step);
}

/**
 * An increasing counting run of `count` terms with a constant `step`, last
 * term ≤ `limit`. With `offset = 0` the terms are multiples of the step
 * (the friendly "10, 20, 30" case); a non-zero offset (< step) shifts the run
 * off the multiples ("3, 13, 23"). The first term is always ≥ 1.
 * Callers keep `limit ≥ (count + 1)·step` so there is always room.
 */
export function countingRun(rng: Rng, step: number, count: number, limit: number, offset: number): number[] {
  const kMin = offset === 0 ? 1 : 0;
  const kMax = Math.floor((limit - offset) / step) - (count - 1);
  const first = rng.int(kMin, Math.max(kMin, kMax)) * step + offset;
  return Array.from({ length: count }, (_, i) => first + i * step);
}

/** `a, b, c, □` — the "what comes next?" layout. */
export function sequencePrompt(shown: readonly number[]): MathMarkup {
  return `${shown.join(', ')}, \\box`;
}

/** Letters used for unknowns once Grade 4 introduces them ('x' is avoided: it looks like ×). */
const LETTERS = ['n', 'm', 'p', 'y'] as const;

/** The unknown's letter: always `n` for gentle problems, then some variety so letters feel like placeholders. */
export function unknownLetter(rng: Rng, difficulty: number): string {
  return difficulty < 0.4 ? 'n' : rng.pick(LETTERS);
}

/**
 * Package a problem whose answer is a positive whole number.
 *
 * Distractors are cleaned here so templates can list every misconception
 * without special-casing coincidences: non-integers, negatives, duplicates
 * and the answer itself are dropped. If fewer than two survive, off-by-one
 * slips (answer + 1, answer − 1, answer + 2 — the most common counting error
 * at these grades) top the list up, so there are always at least two.
 */
export function wholeProblem(
  prompt: MathMarkup,
  answer: number,
  distractors: readonly number[],
  instruction?: Instruction,
): GeneratedProblemCore {
  const kept: number[] = [];
  const consider = (v: number) => {
    if (Number.isInteger(v) && v >= 0 && v !== answer && !kept.includes(v)) kept.push(v);
  };
  for (const v of distractors) consider(v);
  for (const v of [answer + 1, answer - 1, answer + 2]) if (kept.length < 2) consider(v);
  return {
    prompt,
    ...(instruction ? { instruction } : {}),
    answer: int(answer),
    answerKind: 'integer',
    distractors: kept.map((v) => int(v)),
  };
}
